<template>
  <section id="education" class="py-20 bg-gray-50 dark:bg-gray-900">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="section-title" :class="{ 'khmer': isKhmer }">
        {{ $t('education.title') }}
      </h2>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Education -->
        <div>
          <h3 class="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
            <AcademicCapIcon class="w-6 h-6 mr-2 text-primary-600" />
            Academic Background
          </h3>
          <div class="space-y-6">
            <div
              v-for="education in educationList"
              :key="education.id"
              class="card p-6 hover:shadow-xl transition-all duration-300"
            >
              <div class="flex items-start justify-between mb-4">
                <div class="flex-1">
                  <h4 class="text-lg font-semibold text-gray-900 dark:text-white">
                    {{ education.degree }}
                  </h4>
                  <p class="text-primary-600 dark:text-primary-400 font-medium">
                    {{ education.institution }}
                  </p>
                  <p class="text-gray-600 dark:text-gray-300 text-sm">
                    {{ education.location }}
                  </p>
                </div>
                <div class="text-right">
                  <div class="inline-flex items-center px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-full">
                    {{ education.year }}
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    GPA: {{ education.gpa }}
                  </div>
                </div>
              </div>
              
              <p class="text-gray-600 dark:text-gray-300 text-sm mb-4">
                {{ education.description }}
              </p>

              <!-- Relevant Coursework -->
              <div v-if="education.coursework">
                <h5 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Relevant Coursework:
                </h5>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="course in education.coursework"
                    :key="course"
                    class="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded"
                  >
                    {{ course }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Certifications -->
        <div>
          <h3 class="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
            <StarIcon class="w-6 h-6 mr-2 text-primary-600" />
            Certifications & Awards
          </h3>
          <div class="space-y-6">
            <div
              v-for="cert in certifications"
              :key="cert.id"
              class="card p-6 hover:shadow-xl transition-all duration-300"
            >
              <div class="flex items-start justify-between mb-4">
                <div class="flex-1">
                  <h4 class="text-lg font-semibold text-gray-900 dark:text-white">
                    {{ cert.title }}
                  </h4>
                  <p class="text-primary-600 dark:text-primary-400 font-medium">
                    {{ cert.issuer }}
                  </p>
                  <p class="text-gray-600 dark:text-gray-300 text-sm">
                    {{ cert.type }}
                  </p>
                </div>
                <div class="text-right">
                  <div class="inline-flex items-center px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm rounded-full">
                    {{ cert.date }}
                  </div>
                  <div v-if="cert.credentialId" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    ID: {{ cert.credentialId }}
                  </div>
                </div>
              </div>
              
              <p class="text-gray-600 dark:text-gray-300 text-sm mb-4">
                {{ cert.description }}
              </p>

              <!-- Skills covered -->
              <div v-if="cert.skills">
                <h5 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Skills Covered:
                </h5>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="skill in cert.skills"
                    :key="skill"
                    class="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded"
                  >
                    {{ skill }}
                  </span>
                </div>
              </div>

              <!-- Verification Link -->
              <div v-if="cert.verificationUrl" class="mt-4">
                <a
                  :href="cert.verificationUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  <LinkIcon class="w-4 h-4 mr-1" />
                  Verify Certificate
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import {
  AcademicCapIcon,
  StarIcon,
  LinkIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const isKhmer = computed(() => store.getters.isKhmer)

const educationList = [
  {
    id: 1,
    degree: 'Bachelor of Science in Computer Science',
    institution: 'Royal University of Phnom Penh',
    location: 'Phnom Penh, Cambodia',
    year: '2014 - 2018',
    gpa: '3.8/4.0',
    description: 'Focused on software engineering, web development, and database systems. Graduated with honors and received the Outstanding Student Award.',
    coursework: [
      'Data Structures & Algorithms',
      'Web Development',
      'Database Systems',
      'Software Engineering',
      'Computer Networks',
      'Mobile App Development'
    ]
  },
  {
    id: 2,
    degree: 'High School Diploma',
    institution: 'Preah Sisowath High School',
    location: 'Phnom Penh, Cambodia',
    year: '2011 - 2014',
    gpa: '3.9/4.0',
    description: 'Completed with distinction in Mathematics and Science. Active member of the Computer Club and Science Olympiad team.',
    coursework: [
      'Advanced Mathematics',
      'Physics',
      'Chemistry',
      'Computer Science',
      'English Literature'
    ]
  }
]

const certifications = [
  {
    id: 1,
    title: 'AWS Certified Solutions Architect',
    issuer: 'Amazon Web Services',
    type: 'Professional Certification',
    date: '2023',
    credentialId: 'AWS-CSA-2023-001',
    description: 'Demonstrated expertise in designing distributed systems on AWS with focus on scalability, security, and cost optimization.',
    skills: ['AWS', 'Cloud Architecture', 'Security', 'Scalability'],
    verificationUrl: 'https://aws.amazon.com/verification'
  },
  {
    id: 2,
    title: 'Vue.js Expert Certification',
    issuer: 'Vue Mastery',
    type: 'Technical Certification',
    date: '2022',
    credentialId: 'VM-VUE-2022-089',
    description: 'Advanced Vue.js certification covering Composition API, state management, testing, and performance optimization.',
    skills: ['Vue.js', 'Composition API', 'Vuex', 'Testing', 'Performance'],
    verificationUrl: 'https://vuemastery.com/verification'
  },
  {
    id: 3,
    title: 'Outstanding Developer Award',
    issuer: 'Cambodia Developer Community',
    type: 'Industry Award',
    date: '2022',
    description: 'Recognized for outstanding contributions to the local developer community and open-source projects.',
    skills: ['Leadership', 'Community Building', 'Open Source']
  },
  {
    id: 4,
    title: 'Google Analytics Certified',
    issuer: 'Google',
    type: 'Digital Marketing Certification',
    date: '2021',
    credentialId: 'GA-2021-567',
    description: 'Proficient in Google Analytics implementation, data analysis, and conversion optimization.',
    skills: ['Analytics', 'Data Analysis', 'Conversion Optimization'],
    verificationUrl: 'https://skillshop.exceedlms.com/verification'
  }
]
</script> 